// Shared UI primitives — one file, one import path, used across every
// restructured page (Dashboard tabs, Devices grid, Monitoring tabs, etc).
// Pairs with the .gc-* classes in src/apple-design.css.
import React, { useMemo, useState } from "react";
import { Search, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

/* ───────────────────────── PillSelect ───────────────────────── */
// A .gc-pill <select> with a real rendered chevron instead of a CSS
// background-image icon (the background-image version tiled itself across
// the whole pill on some renders instead of showing once — a real DOM icon
// can't do that, so it replaces every gc-pill select app-wide).
export function PillSelect({ value, onChange, children, style, className = "" }) {
  return (
    <span className="gc-pill-select-wrap">
      <select
        value={value}
        onChange={onChange}
        className={`gc-pill gc-pill-select ${className}`.trim()}
        style={style}
      >
        {children}
      </select>
      <ChevronDown size={11} className="gc-pill-select-icon" />
    </span>
  );
}

/* ───────────────────────── PageHeader ───────────────────────── */
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="gc-page-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="gc-page-sub">{subtitle}</p>}
      </div>
      {actions && <div className="gc-page-actions">{actions}</div>}
    </div>
  );
}

/* ───────────────────────── Tabs ───────────────────────── */
// tabs: [{ id, label, disabled? }]
export function Tabs({ tabs, activeId, onChange }) {
  if (!tabs || tabs.length <= 1) return null; // a single permitted tab needs no bar
  return (
    <div className="gc-tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={t.id === activeId}
          className={`gc-tab ${t.id === activeId ? "active" : ""}`}
          disabled={t.disabled}
          onClick={() => !t.disabled && onChange(t.id)}
          title={t.disabled ? "Coming soon" : undefined}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ───────────────────────── FilterBar ───────────────────────── */
// Replaces the team-select + name-search block copy-pasted across the
// USB / URL Tracker / File Monitor / File Uploads pages.
export function FilterBar({ search, searchPlaceholder = "Search…", onSearchChange, team, onTeamChange, departments, children }) {
  return (
    <div className="gc-filter-bar">
      <div className="gc-filter-search">
        <Search size={13} />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
        />
      </div>
      {departments && departments.length > 0 && (
        <select
          value={team}
          onChange={(e) => onTeamChange(e.target.value)}
          className="gc-pill"
          style={{ appearance: "none" }}
        >
          <option value="all">All Teams</option>
          {departments.map((d) => (
            <option key={d._id || d.name} value={d.name}>{d.name}</option>
          ))}
        </select>
      )}
      {children}
    </div>
  );
}

/* ───────────────────────── DataGrid ───────────────────────── */
// columns: [{ key, label, sortable?, render?(row), sortFn?(a,b), width? }]
// rows: array of data objects
// getRowId: row => string (defaults to row.id ?? row._id)
// selectable: bool — adds a checkbox column, calls onSelectionChange(Set<id>)
// defaultSort: { key, dir: "asc"|"desc" }
export function DataGrid({
  columns,
  rows,
  getRowId = (r) => r.id ?? r._id,
  selectable = false,
  selectedIds,
  onSelectionChange,
  defaultSort,
  onRowClick,
  emptyState,
}) {
  const [sort, setSort] = useState(defaultSort || null);
  const selected = selectedIds || new Set();

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const cmp = col.sortFn
      ? col.sortFn
      : (a, b) => {
          const av = a[sort.key];
          const bv = b[sort.key];
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          if (typeof av === "number" && typeof bv === "number") return av - bv;
          return String(av).localeCompare(String(bv));
        };
    const out = [...rows].sort(cmp);
    if (sort.dir === "desc") out.reverse();
    return out;
  }, [rows, sort, columns]);

  const toggleSort = (col) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: "asc" };
      if (prev.dir === "asc") return { key: col.key, dir: "desc" };
      return null; // third click clears sort
    });
  };

  const allChecked = selectable && rows.length > 0 && rows.every((r) => selected.has(getRowId(r)));
  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (allChecked) rows.forEach((r) => next.delete(getRowId(r)));
    else rows.forEach((r) => next.add(getRowId(r)));
    onSelectionChange(next);
  };
  const toggleRow = (id) => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  if (rows.length === 0 && emptyState) {
    return <div className="gc-empty">{emptyState}</div>;
  }

  return (
    <div className="gc-table-wrap">
      <table className="gc-table">
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: 34 }}>
                <span
                  className={`gc-checkbox ${allChecked ? "checked" : ""}`}
                  onClick={toggleAll}
                  role="checkbox"
                  aria-checked={allChecked}
                >
                  {allChecked && "✓"}
                </span>
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                className={col.sortable ? "sortable" : ""}
                style={col.width ? { width: col.width } : undefined}
                onClick={() => toggleSort(col)}
              >
                {col.label}
                {col.sortable && (
                  <span className="gc-sort-ic">
                    {sort?.key === col.key
                      ? sort.dir === "asc" ? <ChevronUp size={11} style={{ display: "inline" }} /> : <ChevronDown size={11} style={{ display: "inline" }} />
                      : <ChevronsUpDown size={11} style={{ display: "inline" }} />}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const id = getRowId(row);
            const isSel = selected.has(id);
            return (
              <tr
                key={id}
                className={isSel ? "selected" : ""}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {selectable && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <span
                      className={`gc-checkbox ${isSel ? "checked" : ""}`}
                      onClick={() => toggleRow(id)}
                      role="checkbox"
                      aria-checked={isSel}
                    >
                      {isSel && "✓"}
                    </span>
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────── Badge ───────────────────────── */
// tone: "ok" | "warn" | "bad" | "neutral"
export function Badge({ tone = "neutral", children }) {
  return (
    <span className={`gc-badge ${tone}`}>
      <span className="dot" />
      {children}
    </span>
  );
}
